// init.ts
import { navigateTo } from 'yrv'
import { SettingsRepository } from './modules/eventsApi/settings/settings.repository'
import { SettingsService } from './modules/eventsApi/settings/settings.service'
import { APIService } from './modules/api.service'
import { isFullscreen, selectedPersonQuery, action, pushAct, posCashier, printerParams, notifications, sseCommand } from './components/_common/stores'
import type { AppNotification } from './modules/notificationsApi/notification'
// Import the functions you need from the SDKs you need
import { initializeApp } from 'firebase/app'
import { getMessaging, onMessage } from 'firebase/messaging'
import type { SharePageDto } from './components/_common/share-page.dto'
import { Store } from './modules/infra/store.service'
import { AllowedHoursEntity, AllowedHoursIntervalEntity } from './modules/usersApi/companies/company'
import { tick } from 'svelte'

export default class Init {
    static swRegistration: ServiceWorkerRegistration = null
    static swReloading = false
    static swControlled = false
    static lastInternetCheck = 0
    static firebaseApp
    static broadcast = new BroadcastChannel('zandu-channel-001')
    static notificationsEventSource: EventSource
    static async init() {
        console.log('init...', location.href)
        const sw = this.initServiceWorker()
        this.#setGlobalLogs()
        await this.#loadWindowVariables()
        // chamar loadWindowVariables antes pois muitas variáveis são necessárias nas chamadas abaixo.
        this.fullscreenMonitor()
        this.reloadOnRotation()
        //
        //await Promise.all([this.#refreshCachedToken(), this.#loadWindowConfigs()])

        //
        window.api.users.statistics.tick('app.load') // sem await propositalmente.
        window.api.users.companies.updateAppVersion()
        this.#tickPlatformStats()
        this.#evaluateNavigations()
        this.#configureFireBase()
        this.#configureNotificationsStream()
        this.#startBroadcastMonitor()
        //
        const promises = []
        promises.push(this.#refreshCachedToken()) // este refresh ocorre por segurança. não é crítico para funcionamento, por isso pode ter concorrência
        promises.push(this.#loadWindowConfigs())
        promises.push(this.#loadFeatureFlags())
        promises.push(this.#loadCurrentCashier())
        await Promise.all(promises)
        //
        await this.#loadTheme()
        this.#tickThemeStat() // depois do loadTheme, pra ler a classe já aplicada no body
        setTimeout(async () => await this.#setPosPrintVersion(), 1800) // como não precisa ser carregado imediatamente, sofre um atraso (não roda nas promise.all pois gera concorrência chamando /flags)
        //
        this.allowedHoursMonitor() //
    }

    // contexto de plataforma/versão — 1 tick por carga do app, junto do app.load.
    static #tickPlatformStats() {
        try {
            window.api.users.statistics.tick(window.api.utils.utils.isMobile() ? 'platform.mobile' : 'platform.desktop')
            if (window.api.utils.utils.isInstalled()) window.api.users.statistics.tick('platform.installed')
            if (window.api.utils.utils.isIOS()) window.api.users.statistics.tick('platform.ios')
            //
            const appVersion = window.store?.company?.appVersion
            if (appVersion) {
                window.api.users.statistics.tick('app.version.' + appVersion.toString().replace(/[^a-zA-Z0-9]+/g, '_'))
            }
        } catch (error) {}
    }

    // separado do tickPlatformStats pois depende da classe theme_dark/theme_light já aplicada no body
    // (só acontece depois do #loadTheme). Sem classe nenhuma = escuro, que é o padrão do app.
    static #tickThemeStat() {
        try {
            const body = document.getElementsByTagName('body')[0]
            const isLight = body?.classList.contains('theme_light')
            window.api.users.statistics.tick(isLight ? 'theme.light' : 'theme.dark')
        } catch (error) {}
    }

    static async #refreshCachedToken() {
        if (!window?.store?.token?.token) return
        await window.api.users.auth.refreshToken(window.store.token.token, true)
    }

    static async allowedHoursMonitor() {
        let cron = async () => {
            if (!window?.store?.user?.userId) return
            let allows
            try {
                allows = await window.api.users.companies.getAllowLogin(window?.store?.user?.userId)
            } catch (error) {
                return
            }
            //console.log('hours checking', allows)
            if (!allows) return
            if (!allows.allowedHours?.active) return
            if (allows.allowLoginNow) {
                if (location.href.includes('user-restricted')) {
                    location.href = '/'
                }
                let day: AllowedHoursIntervalEntity = allows.allowedHours?.interval[new Date().getDay()]
                if (!day) return
                const remaining = day.toTime - (new Date().getHours() * 60 + new Date().getMinutes())
                console.log('remaining', allows, remaining)
                if (remaining < 10 && day.toTime < 1439) {
                    window.alert('Atenção: seu acesso será bloqueado em menos de 10 minutos por restrições de horário.')
                }
                return
            }
            if (location.href.includes('user-restricted')) return
            console.log('block now')
            setTimeout(() => {
                location.href = '/user-restricted'
            }, 5000)
        }
        setInterval(cron, 7 * 60 * 1000)
        setTimeout(cron, 1500)
    }

    static async #loadCurrentCashier() {
        if (window.configs?.allowCashierStatus) {
            const cashier = await window.api.pos.cashiers.opened()
            //console.log('cashier', cashier)
            posCashier.update((_) => cashier)
        }
    }

    static async checkInternet(e) {
        try {
            if (!e?.reason?.message.includes('Failed to fetch')) return
            const now = new Date().getTime()
            if (now - 30000 < this.lastInternetCheck) return
            this.lastInternetCheck = now
            const netStatus = await window.api.utils.internet.check(undefined, 3, 500, 1200)
            if (!netStatus.stability) {
                window.error(
                    'Erro de conexão. Verifique sua internet (2).\nApós restabelecer atualize a tela.\nEstabilidade da Internet: ' +
                        netStatus.level?.toFixed(0) +
                        '%',
                )
            } else {
                window.error('Erro ao conectar com o servidor (2).\nPode ter ocorrido uma instabilidade temporária nos servidores do Zandu.\nTente novamente.')
            }
        } catch (error) {}
    }

    static #setGlobalLogs() {
        const err = async (e) => {
            await Init.checkInternet(e)
            // alguns erros podem ser ignorados
            if (e?.reason?.message?.includes('messaging/unsupported-browser')) return
            //
            if (window.location.href.includes('127.0.0.1')) return
            if (window['newrelic']) {
                try {
                    console.log('global', e, e?.reason)
                    window['newrelic'].log(e?.reason?.message, {
                        level: 'error',
                        customAttributes: {
                            context: 'global_catch',
                            companyId: window.store?.token?.companyId,
                            _EXTRA_: { type: e?.type, stack: e?.reason?.stack },
                            path: location.href,
                            isIOS: window?.api?.utils?.utils?.isIOS(),
                            isInstalled: window?.api?.utils?.utils?.isInstalled(),
                            isMobile: window?.api?.utils?.utils?.isMobile(),
                            userAgent: navigator.userAgent,
                        },
                    })
                } catch (error) {
                    console.warn('global_catch failed', e)
                }
            }
        }
        //
        window.addEventListener('error', err)
        window.addEventListener('unhandledrejection', err)
        window.addEventListener('uncaughtexception', err)
    }

    static async #setPosPrintVersion() {
        try {
            const posPrintVersionLatest = await window.flags('posPrintVersionLatest')
            if (posPrintVersionLatest) return
            const found = await window.api.tools.documents.getAll()
            if (!found) return
            await window.api.events.settings.updateConfigs({ posPrintVersionLatest: found.length === 0 })
            await window.flags('posPrintVersionLatest', null, true)
        } catch (error) {}
    }

    static async #loadTheme() {
        const theme = await window.flags('theme', true)
        if (theme) window.api.utils.utils.loadTheme(theme)
    }

    static #startBroadcastMonitor() {
        this.broadcast.onmessage = (event) => {
            console.log('Message received from broadcast inside init')
            if (event.data && event.data.act === 'push') {
                this.#evaluatePushMessage(event.data.value)
            }
        }
    }

    static initServiceWorker() {
        if (!('serviceWorker' in navigator)) return
        this.swControlled = !!navigator.serviceWorker.controller

        const registrar = async () => {
            try {
                this.swRegistration =
                    (await navigator.serviceWorker.getRegistration('/')) || (await navigator.serviceWorker.register('/service-worker.js', { scope: '/' }))

                console.log('[SW] registrado', this.swRegistration.scope)

                navigator.serviceWorker.addEventListener('controllerchange', () => {
                    // primeira instalação: a página não tinha controller e o clients.claim() só a adotou — o código
                    // carregado já é o atual, não há o que recarregar. Recarregar aqui vira laço infinito em navegador
                    // que não guarda o registro do SW entre cargas (toda carga vira "primeira instalação").
                    const wasControlled = this.swControlled
                    this.swControlled = true
                    if (!wasControlled) return
                    if (this.swReloading) return
                    // trava entre cargas: se o SW trocar de novo logo após a recarga, não recarrega outra vez
                    try {
                        const last = Number(sessionStorage.getItem('sw-reload-at')) || 0
                        if (Date.now() - last < 60 * 1000) return
                        sessionStorage.setItem('sw-reload-at', String(Date.now()))
                    } catch (error) {}
                    this.swReloading = true
                    console.log('[SW] novo controller assumiu, recarregando')
                    location.reload()
                })

                if (this.swRegistration.waiting) {
                    this.swRegistration.waiting.postMessage({ act: 'skip-waiting' })
                }

                this.swRegistration.addEventListener('updatefound', () => {
                    const sw = this.swRegistration.installing
                    if (!sw) return
                    sw.addEventListener('statechange', () => {
                        if (sw.state === 'installed' && navigator.serviceWorker.controller) {
                            sw.postMessage({ act: 'skip-waiting' })
                        }
                    })
                })

                this.#scheduleSwUpdates()
            } catch (error) {
                console.error('[SW] falha ao registrar', error)
            }
        }

        // Em PWA instalado o evento 'load' pode já ter disparado.
        if (document.readyState === 'complete') registrar()
        else window.addEventListener('load', registrar)
    }

    static #scheduleSwUpdates() {
        const checar = async () => {
            if (!this.swRegistration) return
            try {
                await this.swRegistration.update()
            } catch (error) {
                console.warn('[SW] update falhou', error)
            }
        }

        checar()
        setInterval(checar, 30 * 60 * 1000)
        window.addEventListener('online', checar)
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') checar()
        })
    }

    // Botão "Reparar aplicativo" - limpa caches e recarrega sem desregistrar
    // o SW (desregistrar derrubaria o push).
    static async repararApp() {
        try {
            const keys = await caches.keys()
            await Promise.all(keys.map((k) => caches.delete(k)))
            if (this.swRegistration?.active) {
                this.swRegistration.active.postMessage({ act: 'purge' })
            }
        } catch (error) {
            console.error('[SW] reparo falhou', error)
        } finally {
            location.reload()
        }
    }

    static #configureFireBase() {
        // TODO: Add SDKs for Firebase products that you want to use
        // https://firebase.google.com/docs/web/setup#available-libraries

        // Your web app's Firebase configuration
        // For Firebase JS SDK v7.20.0 and later, measurementId is optional
        const firebaseConfig = {
            apiKey: 'AIzaSyB5yh4XWztReExz4GaTIGbzfzoAiwN95Gg',
            authDomain: 'zandu-main.firebaseapp.com',
            projectId: 'zandu-main',
            storageBucket: 'zandu-main.appspot.com',
            messagingSenderId: '482161957283',
            appId: '1:482161957283:web:fe87a13818481da0ede216',
            measurementId: 'G-Z9D7YKD2SR',
        }

        try {
            // Initialize Firebase
            this.firebaseApp = initializeApp(firebaseConfig)
            //const analytics = getAnalytics(this.firebaseApp)
            const messaging = getMessaging(this.firebaseApp)
            // quando uma mensagem vem do servidor, via firebase, aqui ela é detectada. se está em primeiro plano este onMessage é quem a trata.

            onMessage(messaging, (payload) => {
                console.log('Message received onMessage')
                this.#evaluatePushMessage(payload)
            })
        } catch (error) {
            console.log('Error configuring Firebase', error)
        }
    }

    // Notificação in-app (ícone de sino): carrega as não lidas e abre o SSE que avisa em tempo real.
    // O evento SSE só carrega o notificationId — o conteúdo é buscado aqui via GET /notifications/:id.
    static #configureNotificationsStream() {
        if (!window?.store?.token?.token) return
        if (window.api.utils.utils.isMobile()) return // mobile ainda não conecta ao SSE por enquanto
        try {
            window.api.notifications
                .getAll({})
                .then((list: AppNotification[]) => notifications.set(list || []))
                .catch(() => {})

            if (this.notificationsEventSource) this.notificationsEventSource.close()
            this.notificationsEventSource = new EventSource(window.api.notifications.streamUrl())
            this.notificationsEventSource.addEventListener('notification', async (event: MessageEvent) => {
                try {
                    const { notificationId } = JSON.parse(event.data)
                    const notification: AppNotification = await window.api.notifications.getOne(notificationId)
                    notifications.update((list) => [notification, ...list])
                } catch (error) {
                    console.log('Error handling notification stream event', error)
                }
            })
            // comando silencioso: nunca vira notificação visível, só alimenta o sseCommand pra quem quiser reagir
            this.notificationsEventSource.addEventListener('command', (event: MessageEvent) => {
                try {
                    const { act, value } = JSON.parse(event.data)
                    console.log('sseCommand received', act, value)
                    sseCommand.set({ act, value, date: new Date() })
                } catch (error) {
                    console.log('Error handling command stream event', error)
                }
            })
        } catch (error) {
            console.log('Error configuring notifications stream', error)
        }
    }

    static #evaluatePushMessage(payload) {
        console.log('Message evaluated: ', payload)

        if (payload.data) {
            if (payload.data.act) {
                pushAct.set({ date: new Date(), act: payload.data.act, value: payload.data?.value })
            }
        }

        // se está visível o navegador não exibe notificação. então exibimos manualmente
        if (!document.hidden) {
            window.api.utils.utils.showNotification(
                payload.notification.title,
                payload.notification.body || undefined,
                payload.notification.icon || '/favicon.png',
                payload.notification.image || undefined,
            )
        }
    }

    static reloadOnRotation() {
        //
        let reloadRequired = ['agenda/all']
        //
        if (!window.api.utils.utils.isMobile()) return
        const isLandscape = () => window.matchMedia('(orientation: landscape)').matches
        const containsFromArray = (text: string, arr: string[]) => arr.some((v) => text.toLowerCase().includes(v.toLowerCase()))
        window.addEventListener('orientationchange', async () => {
            console.log(isLandscape() ? 'landscape' : 'portrait', location.href)
            if (!containsFromArray(location.href, reloadRequired)) return
            await tick()
            location.reload()
        })
    }

    static fullscreenMonitor() {
        const event = () => {
            let maxHeight = window.screen.height,
                maxWidth = window.screen.width,
                curHeight = window.innerHeight,
                curWidth = window.innerWidth
            if (maxWidth == curWidth && maxHeight == curHeight && screen.width > 900) {
                isFullscreen.set(true)
            } else {
                isFullscreen.set(false)
            }
        }
        window.addEventListener('resize', event)
        event()
    }

    static async #loadFeatureFlags() {
        window.beta = await window.flags('beta')
        window.featureFlags = []
        if (await window.flags('chat')) window.featureFlags.push('chat')
        if (await window.flags('raiox')) window.featureFlags.push('raiox')
        if (await window.flags('partnership')) window.featureFlags.push('partnership')
        if (await window.flags('cam')) window.featureFlags.push('cam')
        if (await window.flags('prospect')) window.featureFlags.push('prospect')
        if (await window.flags('cashier')) window.featureFlags.push('cashier')
        if (await window.flags('evo')) window.featureFlags.push('evo')
        if (await window.flags('uazapi')) window.featureFlags.push('uazapi')
        if (await window.flags('compliance')) window.featureFlags.push('compliance')
        if (await window.flags('grid')) window.featureFlags.push('grid')
    }

    static async #loadWindowConfigs() {
        try {
            window.configs = (await window.api.events.settings.getConfigs())?.configs
        } catch (error) {}
    }

    static async #loadWindowVariables() {
        window.$ = {}
        window.api = new APIService()
        window.store = new Store()
        // user/company flags
        window.flags = async (param: string, user: boolean = false, value?: string) => {
            try {
                if (user) param += '-' + window.store.token.userId
                // if there is no cache or is an update
                if (!window._flags || typeof value !== 'undefined') {
                    if (!window.store.token.companyId) return null
                    const sR = new SettingsRepository()
                    const sS = new SettingsService(sR)
                    if (typeof value !== 'undefined') {
                        let dto: any = {}
                        if (value.toString() === 'true' || value.toString() === 'false') {
                            dto[param] = value.toString() === 'true'
                        } else {
                            dto[param] = value
                        }
                        window._flags = await sS.updateFlags({ flags: dto })
                    } else {
                        window._flags = await sS.getFlags()
                    }
                }
            } catch (error) {
                console.error(error)
            }
            try {
                return window._flags['flags'][param]
            } catch (error) {
                return null
            }
        }

        window.person = (act: 'search' | 'new' | 'show', query: string = null, tab: number = 1) => {
            selectedPersonQuery.update((_) => {
                if (!act) {
                    action.update((_) => null)
                    return null
                }
                action.update((_) => {
                    return { action: 'person', params: { act, query, tab } }
                })
                return { query, tab, act }
            })
        }

        let printer
        window.printer = (params: SharePageDto) => {
            printerParams.update((_) => {
                return params
            })
        }

        window.log = async (message: string, level: 'debug' | 'error' | 'warn' = 'debug', extra: any = undefined) => {
            let log = []
            log.push(message)
            if (extra) log.push(extra)
            if (level === 'error') {
                console.error(...log)
            } else if (level === 'warn') {
                console.warn(...log)
            } else {
                level = 'debug'
                console.log(...log)
            }
            if (window['newrelic']) {
                try {
                    window['newrelic'].log(message, {
                        level,
                        customAttributes: {
                            context: 'window.log',
                            companyId: window.store?.token?.companyId,
                            _EXTRA_: extra,
                            path: location.href,
                            isIOS: window?.api?.utils?.utils?.isIOS(),
                            isMobile: window?.api?.utils?.utils?.isMobile(),
                            isInstalled: await window?.api?.utils?.utils?.isInstalled(),
                        },
                    })
                } catch (error) {
                    window['newrelic'].log(message, {
                        level,
                        customAttributes: {
                            context: 'window.log',
                            companyId: window.store?.token?.companyId,
                            alert: 'newrelic extra log failed',
                            path: location.href,
                        },
                    })
                }
            }
            return
        }
    }

    static #evaluateNavigations() {
        if (window.api.users.auth.isValid()) {
            window.api.users.auth.setTokenToAutoRenew()
            if (!window.api.users.auth.isCompanySelected()) {
                navigateTo('/companies/select')
                return
            }
            return
        }
        if (
            !window.api.users.auth.isValid() &&
            !location.href.includes('/login') &&
            !location.href.includes('/public') &&
            !location.href.includes('/signup') &&
            !location.href.includes('/oauth/authorize')
        ) {
            navigateTo('/login')
            return
        }
    }
}
